import React, { useEffect, useState } from "react";
import { Card, Row, Col, Typography, Select, Spin, Empty, Tooltip, message } from "antd";
import { Link } from "react-router-dom";
import { ArrowLeftOutlined, DollarOutlined, TeamOutlined, CrownOutlined, RiseOutlined, AndroidOutlined, AppleOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { motion } from "framer-motion";
import { Line, Pie, Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    ArcElement,
} from 'chart.js';
import subscriptionService from "../../../services/subscription-service";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ArcElement
);

const { Title: TypographyTitle, Text } = Typography;
const { Option } = Select;

// Theme colors matching adminDashboard
const THEME = {
    primary: '#FE027D',
    primaryLight: '#FF4DA6',
    accent: '#ec4899',
    accentCyan: '#06b6d4',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    text: '#1a1a1a',
    textGray: '#64748b',
    border: 'rgba(254, 2, 125, 0.2)',
    borderLight: 'rgba(254, 2, 125, 0.08)',
};

// Custom Card Component matching adminDashboard style
const DashboardCard = ({ title, value, prefix, gradient, delay = 0, tooltip }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay }}
    >
        <Tooltip title={tooltip} placement="top">
            <motion.div
                whileHover={{ y: -8, scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
                <Card
                    hoverable
                    style={{
                        background: 'var(--dark-bg-card)',
                        border: '1px solid rgba(254, 2, 125, 0.2)',
                        borderRadius: 'var(--radius-xl)',
                        transition: 'var(--transition-normal)',
                        cursor: 'default',
                        backdropFilter: 'blur(20px)',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-card)',
                    }}
                    bodyStyle={{ padding: 'var(--spacing-xl)', position: 'relative', zIndex: 2 }}
                >
                    {/* Background gradient overlay */}
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: gradient,
                        opacity: 0.05,
                        borderRadius: 'var(--radius-xl)',
                        zIndex: 1,
                    }} />

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}>
                        <div style={{ flex: 1 }}>
                            <p style={{
                                color: 'var(--text-gray)',
                                margin: 0,
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}>
                                {title}
                            </p>
                            <h2 style={{
                                color: 'var(--text-white)',
                                margin: '10px 10px 10px 0px',
                                fontSize: '2.0rem',
                                fontWeight: '700',
                                fontFamily: 'Poppins-Bold',
                                lineHeight: 1.2
                            }}>
                                {value}
                            </h2>
                        </div>
                        <div style={{
                            background: gradient,
                            padding: '10px',
                            borderRadius: 'var(--radius-xl)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                            border: '1px solid rgba(255,255,255,0.1)',
                        }}>
                            <div style={{
                                fontSize: '1.5rem',
                                color: 'var(--text-white)',
                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '12px 5px',
                            }}>
                                {prefix}
                            </div>
                        </div>
                    </div>
                </Card>
            </motion.div>
        </Tooltip>
    </motion.div>
);

const SubscriptionAnalytics = () => {
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(12);
    const [analyticsData, setAnalyticsData] = useState(null);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const response = await subscriptionService.getSubscriptionAnalytics({ period });
            if (response.success) {
                setAnalyticsData(response.data);
                message.success(response.message || 'Analytics data loaded successfully');
            } else {
                message.error(response.message || 'Failed to load analytics data');
            }
        } catch (error) {
            console.error('Error fetching subscription analytics:', error);
            message.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, [period]);

    // Summary Cards Data
    const summaryData = analyticsData?.summary || {};
    const revenueGraph = analyticsData?.revenue_graph || { monthly: [], daily: [] };
    const userTypeDistribution = analyticsData?.user_type_distribution || [];
    const subscriptionStatus = analyticsData?.subscription_status || [];
    const platformDistribution = analyticsData?.platform_distribution || [];

    // Revenue Graph Data (Monthly)
    const revenueChartData = {
        labels: revenueGraph.monthly?.map(item => item.month) || [],
        datasets: [
            {
                label: 'Revenue',
                data: revenueGraph.monthly?.map(item => item.revenue) || [],
                borderColor: THEME.primary,
                backgroundColor: 'rgba(254, 2, 125, 0.1)',
                fill: true,
                tension: 0.4,
                yAxisID: 'y',
            },
            {
                label: 'Subscriptions',
                data: revenueGraph.monthly?.map(item => item.subscriptions) || [],
                borderColor: THEME.accentCyan,
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                fill: true,
                tension: 0.4,
                yAxisID: 'y1',
            },
        ],
    };

    const revenueChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: THEME.textGray,
                    font: { size: 12 }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                cornerRadius: 8,
            }
        },
        scales: {
            x: {
                grid: { color: THEME.borderLight },
                ticks: { color: THEME.textGray }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                grid: { color: THEME.borderLight },
                ticks: { 
                    color: THEME.textGray,
                    callback: (value) => '$' + value.toFixed(0)
                },
                title: {
                    display: true,
                    text: 'Revenue ($)',
                    color: THEME.textGray
                }
            },
            y1: {
                type: 'linear',
                display: true,
                position: 'right',
                grid: { drawOnChartArea: false },
                ticks: { color: THEME.textGray },
                title: {
                    display: true,
                    text: 'Subscriptions',
                    color: THEME.textGray
                }
            },
        },
    };

    // User Type Distribution - Doughnut Chart
    const userTypeChartData = {
        labels: userTypeDistribution.map(item => item.type),
        datasets: [
            {
                data: userTypeDistribution.map(item => item.count),
                backgroundColor: [THEME.success, THEME.textGray],
                borderColor: ['rgba(16, 185, 129, 0.4)', 'rgba(100, 116, 139, 0.4)'],
                borderWidth: 2,
                hoverOffset: 12,
                cutout: '65%',
            },
        ],
    };

    // Subscription Status - Pie Chart
    const statusChartData = {
        labels: subscriptionStatus.map(item => item.status),
        datasets: [
            {
                data: subscriptionStatus.map(item => item.count),
                backgroundColor: [THEME.success, THEME.error, THEME.warning],
                borderColor: ['rgba(16, 185, 129, 0.4)', 'rgba(239, 68, 68, 0.4)', 'rgba(245, 158, 11, 0.4)'],
                borderWidth: 2,
                hoverOffset: 12,
            },
        ],
    };

    // Platform Distribution - Bar Chart
    const platformChartData = {
        labels: platformDistribution.map(item => item.platform),
        datasets: [
            {
                label: 'Subscriptions',
                data: platformDistribution.map(item => item.count),
                backgroundColor: [THEME.primary, THEME.accentCyan],
                borderColor: ['rgba(254, 2, 125, 0.8)', 'rgba(6, 182, 212, 0.8)'],
                borderWidth: 2,
                borderRadius: 8,
            },
            {
                label: 'Revenue',
                data: platformDistribution.map(item => item.revenue),
                backgroundColor: [THEME.primaryLight, THEME.accent],
                borderColor: ['rgba(214, 51, 196, 0.8)', 'rgba(236, 72, 153, 0.8)'],
                borderWidth: 2,
                borderRadius: 8,
            },
        ],
    };

    const pieChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: THEME.textGray,
                    font: { size: 12 },
                    padding: 20,
                    usePointStyle: true,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                cornerRadius: 8,
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const percentage = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percent = ((value / percentage) * 100).toFixed(1);
                        return `${label}: ${value} (${percent}%)`;
                    }
                }
            }
        },
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: THEME.textGray,
                    font: { size: 12 },
                    padding: 20,
                    usePointStyle: true,
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                cornerRadius: 8,
            }
        },
        scales: {
            x: {
                grid: { color: THEME.borderLight },
                ticks: { color: THEME.textGray }
            },
            y: {
                grid: { color: THEME.borderLight },
                ticks: { color: THEME.textGray }
            },
        },
    };

    if (loading) {
        return (
            <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '400px',
                background: 'var(--page-bg)',
                borderRadius: 'var(--radius-xl)'
            }}>
                <Spin size="large" tip="Loading analytics..." />
            </div>
        );
    }

    return (
        <motion.div
            className="page-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
        >
            {/* Header */}
            <div className="module-header">
                <div>
                    <Link to="/subscriptions" style={{ color: 'var(--text-gray)', marginBottom: '8px', display: 'inline-block' }}>
                        <ArrowLeftOutlined /> Back to Subscriptions
                    </Link>
                    <TypographyTitle level={2} style={{ margin: 0, color: 'var(--text-white)' }}>
                        Subscription Analytics
                    </TypographyTitle>
                    <Text style={{ color: 'var(--text-gray)' }}>
                        Track revenue, user distribution, and subscription metrics
                    </Text>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Select
                        value={period}
                        onChange={setPeriod}
                        style={{ width: 150 }}
                        className="input-field"
                    >
                        <Option value={3}>Last 3 months</Option>
                        <Option value={6}>Last 6 months</Option>
                        <Option value={12}>Last 12 months</Option>
                    </Select>
                </div>
            </div>

            {/* Summary Cards - AdminDashboard Style */}
            <Row gutter={[24, 24]} style={{ marginBottom: 'var(--spacing-2xl)' }}>
                <Col xs={24} sm={12} lg={8}>
                    <DashboardCard
                        title="Total Revenue"
                        value={`$${(summaryData.total_revenue || 0).toFixed(2)}`}
                        prefix={<DollarOutlined />}
                        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                        delay={0.1}
                        tooltip="Total revenue from subscriptions"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <DashboardCard
                        title="Total Subscriptions"
                        value={summaryData.total_subscriptions || 0}
                        prefix={<CrownOutlined />}
                        gradient="linear-gradient(135deg, #10b981 0%, #059669 100%)"
                        delay={0.2}
                        tooltip="Total number of subscriptions"
                    />
                </Col>
                <Col xs={24} sm={12} lg={8}>
                    <DashboardCard
                        title="Avg Revenue Per Subscription"
                        value={`$${(summaryData.average_revenue_per_subscription || 0).toFixed(2)}`}
                        prefix={<RiseOutlined />}
                        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                        delay={0.3}
                        tooltip="Average revenue per subscription"
                    />
                </Col>
            </Row>

            {/* Revenue Graph */}
            <Row gutter={[24, 24]} style={{ marginBottom: 'var(--spacing-2xl)' }}>
                <Col xs={24}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        <Card
                            title={<span style={{ color: 'var(--text-white)' }}>Revenue & Subscriptions Trend</span>}
                            style={{
                                background: 'var(--dark-bg-card)',
                                border: '1px solid rgba(254, 2, 125, 0.2)',
                                borderRadius: 'var(--radius-xl)',
                            }}
                            bodyStyle={{ padding: '24px', height: '400px' }}
                        >
                            {revenueGraph.monthly?.length > 0 ? (
                                <Line data={revenueChartData} options={revenueChartOptions} />
                            ) : (
                                <Empty description="No revenue data available" />
                            )}
                        </Card>
                    </motion.div>
                </Col>
            </Row>

            {/* Charts Row */}
            <Row gutter={[24, 24]} style={{ marginBottom: 'var(--spacing-2xl)' }}>
                <Col xs={24} lg={8}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.5 }}
                    >
                        <Card
                            title={<span style={{ color: 'var(--text-white)' }}>User Type Distribution</span>}
                            style={{
                                background: 'var(--dark-bg-card)',
                                border: '1px solid rgba(254, 2, 125, 0.2)',
                                borderRadius: 'var(--radius-xl)',
                            }}
                            bodyStyle={{ padding: '24px', height: '350px' }}
                        >
                            {userTypeDistribution.length > 0 ? (
                                <Doughnut data={userTypeChartData} options={pieChartOptions} />
                            ) : (
                                <Empty description="No user type data available" />
                            )}
                        </Card>
                    </motion.div>
                </Col>
                <Col xs={24} lg={8}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.6 }}
                    >
                        <Card
                            title={<span style={{ color: 'var(--text-white)' }}>Subscription Status</span>}
                            style={{
                                background: 'var(--dark-bg-card)',
                                border: '1px solid rgba(254, 2, 125, 0.2)',
                                borderRadius: 'var(--radius-xl)',
                            }}
                            bodyStyle={{ padding: '24px', height: '350px' }}
                        >
                            {subscriptionStatus.length > 0 ? (
                                <Pie data={statusChartData} options={pieChartOptions} />
                            ) : (
                                <Empty description="No status data available" />
                            )}
                        </Card>
                    </motion.div>
                </Col>
                <Col xs={24} lg={8}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.7 }}
                    >
                        <Card
                            title={<span style={{ color: 'var(--text-white)' }}>Platform Distribution</span>}
                            style={{
                                background: 'var(--dark-bg-card)',
                                border: '1px solid rgba(254, 2, 125, 0.2)',
                                borderRadius: 'var(--radius-xl)',
                            }}
                            bodyStyle={{ padding: '24px', height: '350px' }}
                        >
                            {platformDistribution.length > 0 ? (
                                <Bar data={platformChartData} options={barChartOptions} />
                            ) : (
                                <Empty description="No platform data available" />
                            )}
                        </Card>
                    </motion.div>
                </Col>
            </Row>
        </motion.div>
    );
};

export default SubscriptionAnalytics;
